import { computed, effect, Injectable, signal } from '@angular/core';
import { PartialStateUpdater, patchState, signalState } from '@ngrx/signals';
import { TaskTypes } from 'src/app/shared/models/tasks.model';
import { deepEqual } from 'src/app/shared/utils/global.utils';
import {
  defaultSidebarState,
  ISidebarState,
  SidebarStateObject,
} from './model/sidebar-state.model';
import { createStateObject } from './utils/sidebar-state.utils';

@Injectable({
  providedIn: 'root',
})
export class SidebarState {
  private readonly sidebarState = signalState<ISidebarState>(
    createStateObject(defaultSidebarState)
  );

  public readonly wholeState = computed<ISidebarState>(
    () => this.sidebarState(),
    {
      equal: (a, b) => {
        return deepEqual(a, b);
      },
    }
  );
  private readonly _selectionMade = signal<string>('');
  public readonly selectionMade = this._selectionMade.asReadonly();
  public readonly searchString = this.sidebarState.searchString;
  public readonly taskTypesSelection = computed<TaskTypes[]>(
    () => {
      return this.sidebarState.taskTypesSelection();
    },
    {
      equal: (a, b) => {
        return deepEqual(a, b);
      },
    }
  );

  public readonly taskOwnership = this.sidebarState.taskOwnership;
  public readonly activeTab = this.sidebarState.activeTab;
  public readonly collapsed = this.sidebarState.collapsed;
  public initialSearch = '';

  constructor() {
    effect(() => {
      // we allow the user to paste in a url that may already have a search string and a selected task
      // because of that we want to not show the blurred overlay on initial navigation
      const searchString = this.searchString();
      const weHadASearchParamAndTheSearchStringChanged =
        !!this.initialSearch && searchString !== this.initialSearch;
      if (weHadASearchParamAndTheSearchStringChanged) {
        this.initialSearch = ''; // this will only be reset once
      }
      if (this.initialSearch === '') {
        this._selectionMade.set('');
      }
    });
  }

  selectTask() {
    this._selectionMade.set(this.searchString());
  }

  initializeStateFromQueryParams(state: SidebarStateObject) {
    this.updateState(state);
    // if we have a search string, select the task
    if (this.searchString()?.length) {
      this.initialSearch = this.searchString();
      this.selectTask();
    }
  }

  updateState(setters: PartialStateUpdater<ISidebarState>[]): void;
  updateState(state: SidebarStateObject): void;
  updateState(param: unknown): void {
    if (Array.isArray(param)) {
      patchState(this.sidebarState, ...param);
    } else if (param instanceof SidebarStateObject) {
      patchState(this.sidebarState, param);
    } else {
      throw new Error('unknown param type');
    }
  }
}
